import { AfterViewInit, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
    AdminRequestHelper,
    Auth0APIConfig,
    AvailableColumns,
    CreateEditUserTypes,
    DealRecord,
    DealSearchColumns,
    GetType,
    IntAdmPermissions,
    UserAccessService,
    ModalCloseConfig,
    ModalConfigData,
    RequestDealUserRole,
    SharpAuthorizationExpanded,
    SingleFullDealRecord,
    UserActionConfig,
    UserActionEnum,
    UserRecord,
    UserSearchColumns,
} from '../shared';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from '../../core/services/user.service';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { UserAccessModalComponent } from '../user-access-modal/user-access-modal.component';
import { ConfigurationService } from '../../core/services/configuration.service';
import { AccessTypes, AuthService } from '../../core/services/auth.service';
import { UserAvailableColumns } from '../user-table/user-table.column.types';
import { DealAvailableColumns } from '../deal-table/deal-table.column.types';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { FeatureFlagService } from '../../core/services/feature-flag/feature-flag.service';
import { FeatureFlagNames } from '../../core/services/feature-flag/feature-flag.types';

interface DealSearchHelper {
    requestHelper: AdminRequestHelper;
    defaultSortField: string;
    defaultSortDir: string;
    records: UserRecord[] | DealRecord[] | SharpAuthorizationExpanded[];
    totalRecordCount: number;
}

@Component({
    selector: 'app-deals',
    templateUrl: './deals.component.html',
    styleUrls: ['./deals.component.scss'],
})
export class DealsComponent implements OnInit, AfterViewInit {
    public readonly tabSingleDealUserHelper = 0;
    public readonly tabShareholderUserAccessHelper = 1;

    public revokeAccessUser: UserRecord;
    // public showRevokeAccess: boolean;
    public showImpersonateUser: boolean;
    public internalAdminModal: MatDialogRef<UserAccessModalComponent>;
    public permissionCreateUser = IntAdmPermissions.userCreate;
    public permissionAddUser = IntAdmPermissions.userAdd;
    public fetching = false;
    public selectedIndexSingleDeal: number = 0;
    public currentSearchHelper: DealSearchHelper;
    // index: 0 = User Access, 1 = Shareholder User Access
    public singleDealSearchHelpers: DealSearchHelper[] = Array.from({ length: 2 }, (_, index) => ({
        requestHelper: null,
        defaultSortField: index === this.tabSingleDealUserHelper ? 'role' : 'nsContactName',
        defaultSortDir: 'asc',
        records: [],
        totalRecordCount: 0,
    }));
    public multipleDealSearchHelper: DealSearchHelper = {
        requestHelper: null,
        defaultSortField: 'name',
        defaultSortDir: 'asc',
        records: [],
        totalRecordCount: 0,
    };

    // Single Deal ng-component
    public selectedDealId: number;
    public selectedDeal: SingleFullDealRecord;

    // Create/Edit User Component values.
    public createEditUserMode: CreateEditUserTypes;
    public showCreateEditUser: boolean = false;
    public userToEdit: UserRecord = null;
    public userToEditId: string = null;

    public sharpAuthSearchFields: string[] = [
        AvailableColumns.NsContactName,
        AvailableColumns.FirstName,
        AvailableColumns.MiddleName,
        AvailableColumns.LastName,
        AvailableColumns.Shareholder,
    ];
    public shareholderDealUserDisplayedColumns: string[] = [
        AvailableColumns.NsContactName,
        AvailableColumns.FirstName,
        AvailableColumns.MiddleName,
        AvailableColumns.LastName,
        AvailableColumns.Shareholder,
        AvailableColumns.Permission,
        // AvailableColumns.EmailStatus,
        AvailableColumns.Actions,
    ];
    public userDisplayedColumns: string[] = [
        // UserAvailableColumns.NsContactName,
        UserAvailableColumns.FirstName,
        UserAvailableColumns.MiddleName,
        UserAvailableColumns.LastName,
        UserAvailableColumns.Email,
        UserAvailableColumns.Role,
        UserAvailableColumns.Actions,
    ];
    public multipleDealsDisplayedColumns: string[] = [
        DealAvailableColumns.NsId,
        DealAvailableColumns.Name,
        DealAvailableColumns.ProjectName,
        DealAvailableColumns.Seller,
        DealAvailableColumns.Buyer,
        DealAvailableColumns.ClosingDate,
        DealAvailableColumns.Actions,
    ];

    private readonly dealsUrl = '/user-access/deals';
    private readonly usersUrl = '/user-access/users';
    private readonly activityUrl = '/user-access/activity';
    private readonly userAccessUrl = '/user-access';

    private userModalConfigTemplate: MatDialogConfig = {
        panelClass: 'dialog__no-padding',
        disableClose: true,
        minWidth: '500px',
        minHeight: '200px',
        autoFocus: 'first-header',
    };

    constructor(
        private userAccessService: UserAccessService,
        private snackBar: MatSnackBar,
        private userService: UserService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private matDialog: MatDialog,
        private configurationService: ConfigurationService,
        private authService: AuthService,
        private cdr: ChangeDetectorRef,
        private featureFlagService: FeatureFlagService,
    ) {}

    ngOnInit(): void {
        this.initializeComponentProperties();

        this.activatedRoute.queryParams.subscribe({
            next: (params: Params) => {
                this.updateCurrentSearchHelper(params['dealId'], params['tabSelectedSingleDeal']);
                const isEmpty = params && Object.keys(params).length === 0;

                if (isEmpty || params['clear'] === 'true') {
                    this.selectedDeal = null;
                    this.selectedDealId = null;
                    this.selectedIndexSingleDeal = 0;
                    this.currentSearchHelper.requestHelper.sortField = this.currentSearchHelper.defaultSortField;
                    this.currentSearchHelper.requestHelper.sortDir = this.currentSearchHelper.defaultSortDir;
                    return;
                }

                this.currentSearchHelper.requestHelper.srchVal = params['search'] ?? '';
                this.currentSearchHelper.requestHelper.sortDir =
                    params['sortDir'] ?? this.currentSearchHelper.defaultSortDir;
                this.currentSearchHelper.requestHelper.sortField =
                    params['sortField'] ?? this.currentSearchHelper.defaultSortField;
                this.currentSearchHelper.requestHelper.page = params['page'] ?? 0;
                this.currentSearchHelper.requestHelper.size = params['pageSize'] ?? 100;

                if (params['dealId']) {
                    this.selectedIndexSingleDeal = +params['tabSelectedSingleDeal'];
                    this.selectedDealId = params['dealId'];
                    this.currentSearchHelper.requestHelper.did = params['dealId'];
                    this.fetchSingleDeal(this.selectedDealId);

                    if (this.selectedIndexSingleDeal === this.tabSingleDealUserHelper) {
                        this.fetchUsers();
                    } else if (this.selectedIndexSingleDeal === this.tabShareholderUserAccessHelper) {
                        this.fetchShareholders();
                    }
                } else {
                    this.fetchDeals();
                    // To be used soon:
                    // this.fetchRoles();
                }
            },
            error: (error) => this.handleError(error),
        });
    }

    ngAfterViewInit(): void {
        this.cdr.detectChanges();
    }

    public performSearch(searchVal: string) {
        this.currentSearchHelper.requestHelper.srchVal = searchVal && searchVal.trim().length > 0 ? searchVal : null;
        this.currentSearchHelper.requestHelper.page = 0;

        this.router.navigate(['/user-access'], {
            queryParams: {
                search: searchVal,
                page: 0,
            },
            queryParamsHandling: 'merge',
        });
    }

    public tabClickSingleDeal(tabIndex: number) {
        this.updateCurrentSearchHelper(this.selectedDealId, tabIndex);

        this.router.navigate([this.dealsUrl], {
            queryParams: {
                dealId: this.selectedDealId,
                tabSelectedSingleDeal: tabIndex,
                search: this.currentSearchHelper.requestHelper.srchVal,
                sortField: this.currentSearchHelper.requestHelper.sortField,
                sortDir: this.currentSearchHelper.requestHelper.sortDir,
                page: this.currentSearchHelper.requestHelper.page,
                pageSize: this.currentSearchHelper.requestHelper.size,
            },
            queryParamsHandling: 'merge',
        });
    }

    public tabChangeSingleDeal(e: MatTabChangeEvent) {
        this.tabClickSingleDeal(e.index);
    }

    public handlePageEvent(e: PageEvent) {
        this.router.navigate([this.userAccessUrl], {
            queryParams: {
                page: e.pageIndex,
                pageSize: e.pageSize,
            },
            queryParamsHandling: 'merge',
        });
    }

    public handleUserToEditEvent(e: UserRecord) {
        this.createEditUserMode = CreateEditUserTypes.EDIT_USER;
        this.userToEditId = e.id;
        this.showCreateEditUser = true;
    }

    public handleSortChange(s: Sort, tab: number | null) {
        if ((tab != null && tab !== this.selectedIndexSingleDeal) || (tab == null && this.selectedDealId != null)) {
            return;
        }

        this.router.navigate(['/user-access'], {
            queryParams: {
                sortField: s.active,
                sortDir: s.direction,
                clear: null,
            },
            queryParamsHandling: 'merge',
        });
        this.cdr.detectChanges();
    }

    public handleUserAction(config: UserActionConfig) {
        const selectedUser = this.currentSearchHelper.records[config.arrayIndex] as UserRecord;
        const userName = selectedUser.firstName + ' ' + selectedUser.lastName;

        switch (config.action) {
            case UserActionEnum.activity: {
                this.router.navigate([this.activityUrl], {
                    queryParams: {
                        userName,
                    },
                    queryParamsHandling: 'merge',
                });

                break;
            }
            case UserActionEnum.sendPwReset: {
                const tempConfig: MatDialogConfig<ModalConfigData> = {
                    ...this.userModalConfigTemplate,
                    data: {
                        title: 'Send Password Reset?',
                        messageHTML: `Would you like to send a password reset for <b>${userName}</b>?`,
                        userName,
                        dealName: null,
                        action: UserActionEnum.sendPwReset,
                        id: selectedUser.id,
                        email: selectedUser.email,
                    },
                };
                this.openModal(tempConfig);

                break;
            }
            case UserActionEnum.resendInv: {
                const tempConfig: MatDialogConfig<ModalConfigData> = {
                    ...this.userModalConfigTemplate,
                    data: {
                        title: 'Resend Invitation',
                        messageHTML: `Would you like to resend the Deal Dashboard invitation to <b>${userName}</b>?`,
                        userName,
                        dealName: null,
                        action: UserActionEnum.resendInv,
                        id: selectedUser.id,
                    },
                };
                this.openModal(tempConfig);

                break;
            }
            case UserActionEnum.deactivate: {
                const tempConfig: MatDialogConfig<ModalConfigData> = {
                    ...this.userModalConfigTemplate,
                    data: {
                        title: 'Confirm Account Deactivation',
                        messageHTML: `This will revoke all access and deactivate <b>${userName}</b>.`,
                        userName,
                        dealName: null,
                        action: UserActionEnum.deactivate,
                        id: selectedUser.id,
                        deactivateFromUserTable: true,
                    },
                };
                this.openModal(tempConfig);

                break;
            }
            case UserActionEnum.block: {
                const tempConfig: MatDialogConfig<ModalConfigData> = {
                    ...this.userModalConfigTemplate,
                    data: {
                        title: 'Confirm Authentication Block',
                        messageHTML: `This will “block” the user in Auth0 without modifying deal permissions.`,
                        userName,
                        dealName: null,
                        action: UserActionEnum.block,
                        id: selectedUser.id,
                    },
                };
                this.openModal(tempConfig);

                break;
            }
            case UserActionEnum.unblock: {
                const tempConfig: MatDialogConfig<ModalConfigData> = {
                    ...this.userModalConfigTemplate,
                    data: {
                        title: 'Unblock Authentication',
                        messageHTML: `This will remove the Blocked status in Auth0, allowing users to authenticate.`,
                        userName,
                        dealName: null,
                        action: UserActionEnum.unblock,
                        id: selectedUser.id,
                    },
                };
                this.openModal(tempConfig);

                break;
            }
        }
    }

    public handleUserClickedEventDealsUsers(uid: number) {
        // will need to pass in the real uid when it exists on the record
        this.router.navigate([this.usersUrl], {
            queryParams: { userId: uid },
            // queryParams: { userId: this.userRecords[0].id },
            queryParamsHandling: 'merge',
        });
    }

    public handleShareholderClickedEventDealsUsers(sid: number) {
        this.router.navigate([this.activityUrl], {
            queryParams: { sid: sid },
            queryParamsHandling: 'merge',
        });
    }

    public showCreateEditUserPanel() {
        this.createEditUserMode = CreateEditUserTypes.CREATE_USER;
        this.showCreateEditUser = true;
    }

    public showAddExistingUserPanel() {
        this.createEditUserMode = CreateEditUserTypes.ADD_EXISTING_USER;
        this.showCreateEditUser = true;
    }

    public handleAddExistingUserButtonClick() {
        this.handleShowCreateEditUserCancelEvent();
        this.showAddExistingUserPanel();
    }

    public handleNewUserButtonClick() {
        this.handleShowCreateEditUserCancelEvent();
        this.showCreateEditUserPanel();
    }
    //
    // public handleRevokeAccessEventDD(user: UserRecord) {
    //     this.revokeAccessUser = user;
    //     const modalConfig: MatDialogConfig<ModalConfigData> = {
    //         ...this.userModalConfigTemplate,
    //         data: {
    //             title: 'Confirm Revoke User Access',
    //             action: UserActionEnum.revokeAccessDD,
    //             messageHTML: "This will remove the user's assigned role to this deal.",
    //             id: user.id,
    //         },
    //     };
    //     this.openModal(modalConfig);
    // }

    public handleRevokeAccessEventSH(userAccess: SharpAuthorizationExpanded) {
        const modalConfig: MatDialogConfig<ModalConfigData> = {
            ...this.userModalConfigTemplate,
            data: {
                title: 'Confirm Revoke User Access',
                action: UserActionEnum.revokeAccessSH,
                messageHTML: "This will remove the shareholder's assigned role to this deal and user.",
                userAccessRecord: userAccess,
            },
        };
        this.openModal(modalConfig);
    }

    public handleShowCreateEditUserCancelEvent() {
        this.createEditUserMode = null;
        this.userToEdit = null;
        this.userToEditId = null;
        this.showCreateEditUser = false;

        this.currentSearchHelper.requestHelper.did = this.selectedDealId;
        this.fetchUsers();
    }

    public dealActivityClick(name?: string) {
        this.router.navigate([this.activityUrl], {
            queryParams: {
                search: name ? name : this.selectedDeal.name,
                filterBy: 'deals',
            },
            queryParamsHandling: 'merge',
        });
    }

    public dealDashboardClick(did?: number) {
        console.log('*** DealsComponent.dealDashboardClick()');
        console.log('*** DealsComponent.dealDashboardClick()');
    }

    public handleDealActivityNavigation($event) {
        const selectedDeal = this.currentSearchHelper.records[$event] as DealRecord;
        this.dealActivityClick(selectedDeal.name);
    }

    public handleDealDashboardNavigation($event) {
        const selectedDeal = this.currentSearchHelper.records[$event] as DealRecord;
        this.dealDashboardClick(selectedDeal.id);
    }

    public openInNewTab($event, url: string) {
        // This is to prevent the click event from propagating up.
        $event?.stopPropagation();
        $event?.preventDefault();
        window.open(url, '_blank').focus();
    }

    public getDealNsUrl(nsId: number): string {
        const nsContactUrl = this.configurationService.envConfig.getValue().nsCustomerUrl;

        return nsContactUrl && nsId ? nsContactUrl.replace(/INSERTIDHERE/, nsId.toString()) : '';
    }

    public getDealSfUrl(sfId: string): string {
        const sfOpportunityUrl = this.configurationService.envConfig.getValue().sfOpportunityUrl;

        return sfOpportunityUrl && sfId ? sfOpportunityUrl.replace(/INSERTIDHERE/, sfId) : '';
    }

    public showAddUserMenu(): boolean {
        return (
            this.authService.checkAccess(this.permissionCreateUser) === AccessTypes.FULL ||
            this.authService.checkAccess(this.permissionAddUser) === AccessTypes.FULL
        );
    }

    public canAccessTabDD(): boolean {
        return this.featureFlagService.isFeatureFlagEnabled(FeatureFlagNames.intappsDealDetailDealDashboardTab);
    }

    public canAccessTabSH(): boolean {
        return this.featureFlagService.isFeatureFlagEnabled(FeatureFlagNames.intappsDealDetailShareholderPortalTab);
    }

    public canAccessImpersonateDealDashboard(): boolean {
        return this.featureFlagService.isFeatureFlagEnabled(FeatureFlagNames.intappsDealDetailImpersonateDealDashboard);
    }

    private initializeComponentProperties() {
        // this.showRevokeAccess = true;
        this.showImpersonateUser = true;

        this.setupSearchHelper(this.multipleDealSearchHelper, GetType.deals, DealSearchColumns);
        this.setupSearchHelper(
            this.singleDealSearchHelpers[this.tabSingleDealUserHelper],
            GetType.users,
            UserSearchColumns,
        );
        this.setupSearchHelper(
            this.singleDealSearchHelpers[this.tabShareholderUserAccessHelper],
            GetType.shareholderUserAccess,
            this.sharpAuthSearchFields,
        );

        // To be used soon:
        // this.requestHelperDealUserAccess = new AdminRequestHelper(GetType.dealUserAccess);
        // this.requestHelperRoles = new AdminRequestHelper(GetType.roles);
    }

    private setupSearchHelper(searchHelper: DealSearchHelper, getType: GetType, searchColumns: string[]) {
        searchHelper.requestHelper = new AdminRequestHelper(getType);
        searchHelper.requestHelper.srchFields = searchColumns;
        searchHelper.requestHelper.sortField = searchHelper.defaultSortField;
        searchHelper.requestHelper.sortDir = searchHelper.defaultSortDir;
    }

    private fetchSingleDeal(did: number) {
        this.fetching = true;
        this.selectedDealId = did;

        // retrieve all values for a single deal record, which has joins to other
        // tables not required by the deals table. Since pagination, sorting, and
        // searching are not required, a new request helper is created to leave the
        // original untouched.
        const tempRequestHelper = new AdminRequestHelper(GetType.deals);
        tempRequestHelper.id = did;

        this.userAccessService.getDeals(tempRequestHelper).subscribe({
            next: (res: any) => {
                this.fetching = false;
                if (res.body && Array.isArray(res.body) && res.body.length === 1) {
                    this.selectedDeal = res.body[0];
                }
            },
            error: (error) => this.handleError(error),
        });
    }

    // Get the current search helper based on the route
    private updateCurrentSearchHelper(dealId: string | number, tabSelectedSingleDeal: string | number) {
        if (!dealId) {
            this.currentSearchHelper = this.multipleDealSearchHelper;
            return;
        }
        const currentTab = Number.isInteger(+tabSelectedSingleDeal)
            ? (+tabSelectedSingleDeal as 0 | 1)
            : this.tabSingleDealUserHelper;
        this.currentSearchHelper = this.singleDealSearchHelpers[currentTab];
    }

    private fetchDeals() {
        this.fetching = true;
        this.userAccessService.getDeals(this.multipleDealSearchHelper.requestHelper).subscribe({
            next: (res: any) => {
                this.fetching = false;
                const contentRange = res.headers.get('content-range'); // "1-3/3"
                this.multipleDealSearchHelper.totalRecordCount = +contentRange.substring(contentRange.indexOf('/') + 1);
                this.multipleDealSearchHelper.records = res.body;
            },
            error: (error) => this.handleError(error),
        });
    }

    private fetchUsers() {
        this.userAccessService
            .getUsers(this.singleDealSearchHelpers[this.tabSingleDealUserHelper].requestHelper)
            .subscribe({
                next: (res: any) => {
                    const contentRange = res.headers.get('content-range'); // "1-3/3"
                    this.singleDealSearchHelpers[this.tabSingleDealUserHelper].totalRecordCount =
                        +contentRange.substring(contentRange.indexOf('/') + 1);
                    this.singleDealSearchHelpers[this.tabSingleDealUserHelper].records = res.body;
                    // To be used soon:
                    // if (this.selectedDealId) {
                    //     this.fetchDealUserAccessRecords(this.selectedDealId);
                    // }
                },
                error: (error) => this.handleError(error),
            });
    }

    /**
     * The "single shareholder" comes from the original list of shareholders,
     * so this search is to retrieve the linked deals and users.
     * @private
     */
    private fetchShareholders() {
        this.fetching = true;

        this.userAccessService
            .getShareholders(this.singleDealSearchHelpers[this.tabShareholderUserAccessHelper].requestHelper)
            .subscribe({
                next: (res: any) => {
                    this.fetching = false;
                    const contentRange = res.headers.get('content-range'); // "1-3/3"
                    this.singleDealSearchHelpers[this.tabShareholderUserAccessHelper].totalRecordCount =
                        +contentRange.substring(contentRange.indexOf('/') + 1);
                    this.singleDealSearchHelpers[this.tabShareholderUserAccessHelper].records = res.body;
                },
                error: (error) => this.handleError(error),
            });
    }

    // To be used soon:
    // private fetchDealUserAccessRecords(dealId: number) {
    //     this.requestHelperDealUserAccess.did = dealId;
    //     this.userAccessService.getDealUserAccessRecords(this.requestHelperDealUserAccess).subscribe({
    //         next: (res: any) => {
    //             this.dealUserAccessRecords = res.body;
    //             // for (let i = 0; i < userIds.length; i++) {
    //             //     const foundIndex = this.userRecords.findIndex((deal) => deal.id === userIds[i]);
    //             //     const foundRoleId = this.dealUserAccessRecords.find(
    //             //         (dealUserAccess) => dealUserAccess.child_id == userIds[i],
    //             //     ).role_id;
    //             //     this.userRecords[foundIndex].role = this.roleRecords.find((role) => role.id == foundRoleId).name;
    //             // }
    //         },
    //         error: (error) => this.handleError(error),
    //     });
    // }

    // To be used soon:
    // private fetchRoles() {
    //     this.userAccessService.getRoles(this.requestHelperRoles).subscribe({
    //         next: (res: any) => {
    //             this.roleRecords = res.body;
    //         },
    //         error: (error) => this.handleError(error),
    //     });
    // }

    private openModal(modalConfig: MatDialogConfig) {
        this.internalAdminModal = this.matDialog.open(UserAccessModalComponent, modalConfig);
        this.internalAdminModal.componentInstance.confirmButtonEvent.subscribe((emmitedValue: ModalCloseConfig) => {
            this.internalAdminModal.close();

            const auth0Config: Auth0APIConfig = {
                userId: emmitedValue.id,
                action: modalConfig.data.action,
                byId: this.userService.user.sub,
                byName: this.userService.user.name,
            };

            switch (emmitedValue.action) {
                case UserActionEnum.sendPwReset: {
                    this.userAccessService.userAuth0(auth0Config).subscribe({
                        next: (res: any) => {
                            if (res.status >= 200 && res.status <= 204) {
                                const msg = `Email sent with password reset link.`;
                                this.snackBar.open(msg, undefined, {
                                    duration: 6000,
                                });
                            } else {
                                const msg = `Unable to send password reset for ${modalConfig.data.userName}. Error: ${res.message}`;
                                console.error(msg);
                                this.snackBar.open(msg, undefined, {
                                    duration: 10000,
                                });
                            }
                        },
                        error: (error) => this.handleError(error),
                    });

                    break;
                }
                case UserActionEnum.resendInv: {
                    this.userAccessService.userAuth0(auth0Config).subscribe({
                        next: (res: any) => {
                            if (res.status >= 200 && res.status <= 204) {
                                this.snackBar.open(`Invitation to ${modalConfig.data.userName} resent`, undefined, {
                                    duration: 6000,
                                });
                            } else {
                                const msg = `Unable to resend the invitation to ${modalConfig.data.userName}. Error: ${res.message}`;
                                console.error(msg);
                                this.snackBar.open(msg, undefined, {
                                    duration: 10000,
                                });
                            }
                        },
                        error: (error) => this.handleError(error),
                    });

                    break;
                }
                case UserActionEnum.deactivate: {
                    this.userAccessService.userAuth0(auth0Config).subscribe({
                        next: (res: any) => {
                            if (res.status >= 200 && res.status <= 204) {
                                this.snackBar.open(`${modalConfig.data.userName} has been deactivated.`, undefined, {
                                    duration: 6000,
                                });

                                if (this.selectedDeal) {
                                    this.fetchUsers();
                                }
                            } else {
                                const msg = `Unable to deactivate ${modalConfig.data.userName}. Error: ${res.message}`;
                                console.error(msg);
                                this.snackBar.open(msg, undefined, {
                                    duration: 10000,
                                });
                            }
                        },
                        error: (error) => this.handleError(error),
                    });

                    break;
                }
                case UserActionEnum.block: {
                    this.userAccessService.userAuth0(auth0Config).subscribe({
                        next: (res: any) => {
                            if (res.status >= 200 && res.status <= 204) {
                                this.snackBar.open(`User Authentication Blocked`, undefined, {
                                    duration: 6000,
                                });

                                if (this.selectedDeal) {
                                    this.fetchUsers();
                                }
                            } else {
                                const msg = `Unable to block ${modalConfig.data.userName}. Error: ${res.message}`;
                                console.error(msg);
                                this.snackBar.open(msg, undefined, {
                                    duration: 10000,
                                });
                            }
                        },
                        error: (error) => this.handleError(error),
                    });

                    break;
                }
                case UserActionEnum.unblock: {
                    this.userAccessService.userAuth0(auth0Config).subscribe({
                        next: (res: any) => {
                            if (res.status >= 200 && res.status <= 204) {
                                this.snackBar.open(`User Authentication Unblocked`, undefined, {
                                    duration: 6000,
                                });

                                if (this.selectedDeal) {
                                    this.fetchUsers();
                                }
                            } else {
                                const msg = `Unable to unblock ${modalConfig.data.userName}. Error: ${res.message}`;
                                console.error(msg);
                                this.snackBar.open(msg, undefined, {
                                    duration: 10000,
                                });
                            }
                        },
                        error: (error) => this.handleError(error),
                    });

                    break;
                }
                // case UserActionEnum.revokeAccessDD: {
                //     const config: RequestDealUserRole = {
                //         dealId: this.selectedDealId,
                //         userId: this.revokeAccessUser.id,
                //         byId: this.userService.user.sub,
                //         byName: this.userService.user.name,
                //     };
                //     this.userAccessService.deleteUserFromDeal(config).subscribe({
                //         next: (res: any) => {
                //             if (res.status >= 200 && res.status <= 204) {
                //                 this.handleShowCreateEditUserCancelEvent();
                //                 const msg = 'User access revoked';
                //                 this.snackBar.open(msg, undefined, {
                //                     duration: 10000,
                //                 });
                //             } else {
                //                 const msg = 'Revoke access failed';
                //                 console.error(msg);
                //                 this.snackBar.open(msg, undefined, {
                //                     duration: 10000,
                //                 });
                //             }
                //         },
                //         error: (error) => this.handleError(error),
                //     });
                //
                //     break;
                // }
                case UserActionEnum.revokeAccessSH: {
                    this.userAccessService.deleteShareholderUserAccess(modalConfig.data.userAccessRecord).subscribe({
                        next: (res: any) => {
                            if (res.status >= 200 && res.status <= 204) {
                                this.snackBar.open('Shareholder access revoked.', undefined, {
                                    duration: 6000,
                                    panelClass: ['swatch-teal'],
                                });
                            } else {
                                const msg = `Revoke access failed`;
                                console.error(msg);
                                this.snackBar.open(msg, undefined, {
                                    duration: 10000,
                                });
                            }
                        },
                        error: (error) => this.handleError(error),
                    });

                    break;
                }
            }
        });
        this.internalAdminModal.componentInstance.cancelButtonEvent.subscribe((emmitedValue) => {
            if (emmitedValue) {
                this.internalAdminModal.close();
            }
        });
    }

    private handleError(error: HttpErrorResponse): void {
        this.fetching = false;
        error['stack'] ? console.error(error['stack']) : console.error(error);

        let msg: string;
        if (error?.status >= 400 && error?.status < 500) {
            msg = error?.error?.message ? error.error.message : 'Server error';
        } else {
            msg = 'Server error';
        }

        this.snackBar.open(msg, undefined, {
            duration: 10000,
        });
    }
}
